package com.swetdot.swetgram.ui.auth

import com.swetdot.swetgram.data.api.ApiError
import com.swetdot.swetgram.protocol.dto.ValidationErrorItem

/**
 * What an auth screen needs to render, independent of Compose so it can be driven from a test.
 */
data class AuthUiState(
    val busy: Boolean = false,
    /** A message to show above the form -- a server rejection rather than a field problem. */
    val banner: AuthBanner? = null,
    /** Field-level problems, keyed by the contract's field names. */
    val fieldErrors: Map<String, FieldError> = emptyMap(),
)

sealed interface AuthBanner {
    /** Wrong username/password, or an expired code. */
    data object InvalidCredentials : AuthBanner

    data object CodeRejected : AuthBanner

    /**
     * A moderator has suspended this account.
     *
     * Separate from [InvalidCredentials] because the two send the reader in opposite directions.
     * "Those details did not match an account" invites another try and then a password reset, and
     * neither can ever work here -- the details matched perfectly. Telling someone to fix a
     * password that is not broken is its own small cruelty, and it also buries the one fact they
     * need in order to do anything about it.
     */
    data object AccountSuspended : AuthBanner

    /** 426 -- this build is below the supported minimum and cannot proceed at all. */
    data object UpgradeRequired : AuthBanner

    data object Offline : AuthBanner

    data class RateLimited(val retryAfterSeconds: Long?) : AuthBanner

    /** Anything unexpected. Carries no server text: ERRORS.md says clients localize by code. */
    data object Unexpected : AuthBanner
}

/**
 * Turns an [ApiError] into something a form can display.
 *
 * Validation errors are unpacked onto the fields the server named, so "that username is taken"
 * appears under the username box rather than as a banner the user has to map back themselves.
 */
fun ApiError.toAuthState(): AuthUiState = when (this) {
    is ApiError.Validation -> AuthUiState(fieldErrors = errors.toFieldErrors())
    is ApiError.AccountSuspended -> AuthUiState(banner = AuthBanner.AccountSuspended)
    is ApiError.Unauthorized -> AuthUiState(banner = AuthBanner.InvalidCredentials)
    is ApiError.SessionExpired -> AuthUiState(banner = AuthBanner.InvalidCredentials)
    is ApiError.UpgradeRequired -> AuthUiState(banner = AuthBanner.UpgradeRequired)
    is ApiError.Offline, is ApiError.Timeout -> AuthUiState(banner = AuthBanner.Offline)
    is ApiError.RateLimited -> AuthUiState(banner = AuthBanner.RateLimited(retryAfterSeconds))
    else -> AuthUiState(banner = AuthBanner.Unexpected)
}

/**
 * Maps the contract's `validation_errors` onto local field reasons.
 *
 * Codes the client does not recognise still surface, as [FieldError.Required] against the named
 * field, so a rule added server-side later is visible rather than silently swallowed.
 */
private fun List<ValidationErrorItem>.toFieldErrors(): Map<String, FieldError> =
    associate { item ->
        item.field to when (item.code) {
            "username_taken", "username_reserved" -> FieldError.UsernameReserved
            "email_taken" -> FieldError.EmailInvalid
            "too_short" -> FieldError.PasswordTooShort
            else -> FieldError.Required
        }
    }

// ---------------------------------------------------------------------------- password recovery

/**
 * Where the recovery wizard stands. Three steps, and the middle one is where the code is spent.
 */
enum class RecoveryStep {
    /** Asking for the address. */
    Email,

    /** The code has been requested; asking for it and the new password. */
    Code,

    /** The password was changed. The caller returns to sign-in. */
    Done,
}

/**
 * The recovery flow as a value, so every transition is testable without a Compose tree.
 *
 * The one rule the whole feature turns on lives in [codeSent]: `forgotPassword` is answered with
 * an empty `202 Accepted` whether or not the address belongs to an account -- verified against
 * the deployed server, which returns a byte-identical response for a registered address, an
 * unregistered one and a malformed one. So this state machine ADVANCES on 202 without ever
 * asking whether the account exists, and no screen in the flow may claim that it does. Branching
 * here, or wording the next screen as a confirmation, would turn a signed-out form into an oracle
 * for which of a leaked address list are Swetgram users.
 *
 * The bodies are identical; the response TIMES are not. The server mails the code inline before
 * responding, so a registered address takes seconds and an unregistered one milliseconds. That is
 * a server-side leak this class cannot close -- it is recorded here so nobody reads the paragraph
 * above as a claim that the endpoint is fully blind.
 */
data class RecoveryState(
    val step: RecoveryStep = RecoveryStep.Email,
    /**
     * The address the code was sent to.
     *
     * Kept for two jobs: naming it on the code screen, and prefilling sign-in afterwards, since a
     * password reset revokes every session and the user has to sign in again.
     */
    val email: String = "",
    /** Busy, banner and field errors, in the same shape the auth screens already render. */
    val form: AuthUiState = AuthUiState(),
) {
    /** A request is in flight. Clears the previous failure so a stale banner cannot linger. */
    fun working(): RecoveryState = copy(form = AuthUiState(busy = true))

    /** 202. See the class comment: unconditional, by design. */
    fun codeSent(email: String): RecoveryState =
        RecoveryState(step = RecoveryStep.Code, email = email.trim(), form = AuthUiState())

    /** 204. The password is changed and every session is gone, including this device's. */
    fun finished(): RecoveryState = copy(step = RecoveryStep.Done, form = AuthUiState())

    /**
     * A failure never moves the step. Sending the user forward after a 429 would leave them on a
     * code screen waiting for an email that was never scheduled.
     */
    fun failed(error: ApiError): RecoveryState = copy(form = error.toRecoveryState())

    /** "I used the wrong address" -- back to the first step, with the address still there. */
    fun restart(): RecoveryState = copy(step = RecoveryStep.Email, form = AuthUiState())
}

/**
 * [ApiError.toAuthState] with the reset endpoint's two field names understood.
 *
 * `resetPassword` reports both of its failures as `400 validation_failed` with a
 * `validation_errors` entry -- `token` for a code that is wrong, spent or expired, and
 * `new_password` for one that breaks the password rules. Falling through to the generic mapping
 * would put "Required" under a field the user has filled in, which reads as a bug.
 */
fun ApiError.toRecoveryState(): AuthUiState = when {
    this !is ApiError.Validation -> toAuthState()
    // A 400 that names no field is, on this endpoint, the code: it is the only failure the
    // server raises from inside the transaction.
    errors.isEmpty() -> AuthUiState(fieldErrors = mapOf("token" to FieldError.CodeRejected))
    else -> AuthUiState(fieldErrors = errors.toResetFieldErrors())
}

private fun List<ValidationErrorItem>.toResetFieldErrors(): Map<String, FieldError> =
    associate { item ->
        item.field to when {
            item.field == "token" -> FieldError.CodeRejected
            // The server's own text spans both bounds ("at least 10 characters ..."), and
            // ERRORS.md forbids showing it, so the client says what it can prove and leaves the
            // rest to the rule it already printed under the field.
            item.field == "new_password" -> FieldError.PasswordRejected
            else -> FieldError.Required
        }
    }
